import React, { FunctionComponent } from "react";
import { ACard, AStatusTile } from "@atiautomacao/ati-ui-library";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import { getIconColor } from "../../../Shared/Components/Layout/menu/helpers/getIconColor";
import { MenuItemStatus } from "../../../Shared/Components/Layout/menu/types/MenuItemStatus";
import { useBessPcsModules } from "./BessPcsReactQuery";
import {
    PCS_LEGEND_ITEMS,
    PCS_MODULE_LABEL,
    buildPcsSummary,
    formatModuleNumber,
    resolvePcsModuleStyle,
} from "./BessPcsModuleUtils";
import { BessPcsModule } from "./PcsModuleTypes";

const SECTION_TITLE = "Módulos do Inversor PCS";
const SIMPLE_SECTION_TITLE = "Módulos PCS";
const SIMPLE_HELPER = "Para ver todos acesse os detalhes do BESS";
const EMPTY_MESSAGE = "Não foram encontrados módulos PCS configurados para o equipamento.";
const ERROR_MESSAGE = "Não foi possível carregar os módulos PCS. Tente novamente.";
const TITLE_ICON_COLOR = "#2D5DE7";

export type BessPcsModulesVariant = "detail" | "simple";

// Layout dos tiles. Com poucos módulos (<= LEFT_ALIGN_THRESHOLD) espalhá-los pela largura fica
// esquisito, então alinhamos à esquerda no tamanho compacto. Acima disso, distribuímos por todo o card.
const LEFT_ALIGN_THRESHOLD = 3;

// Grid responsiva: os tiles refluem e ocupam toda a largura do card (quantidade de MODs é dinâmica
// por BESS). auto-fit colapsa colunas vazias, então os módulos existentes se distribuem por todo o card.
const MODULES_GRID_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(76px, 1fr))",
    justifyItems: "stretch",
    gap: "12px",
} as const;

// Poucos módulos: alinhados à esquerda, tamanho compacto (sem espalhar pela largura toda).
const MODULES_LEFT_SX = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: "12px",
} as const;

// Tela simplificada: no máximo 12 por linha; excedentes quebram. Tiles encolhem em card estreito.
const SIMPLE_GRID_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
    gap: "16px",
    overflowX: "hidden",
} as const;

const SIMPLE_TILE_SX = {
    width: "100%",
    minWidth: 0,
    padding: "8px 4px",
} as const;

const resolveModulesLayoutSx = (count: number) =>
    count <= LEFT_ALIGN_THRESHOLD ? MODULES_LEFT_SX : MODULES_GRID_SX;

const Legend: FunctionComponent = () => (
    <Stack direction="row" spacing={2} sx={{ alignItems: "center", padding: "8px", flexWrap: "wrap" }}>
        {PCS_LEGEND_ITEMS.map((item) => (
            <Stack key={item.label} direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                <FiberManualRecordIcon
                    sx={{ color: getIconColor(item.status as MenuItemStatus), fontSize: 20 }}
                />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{item.label}</Typography>
            </Stack>
        ))}
    </Stack>
);

const renderModuleContent = (
    isLoading: boolean,
    isError: boolean,
    hasCachedData: boolean,
    modules: BessPcsModule[],
    layoutSx: Record<string, unknown>,
    tileSx: Record<string, unknown> | undefined,
    skeletonHeight: number
): JSX.Element => {
    if (isLoading) {
        return (
            <Box sx={layoutSx}>
                {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} animation="wave" variant="rounded" width="100%" height={skeletonHeight} />
                ))}
            </Box>
        );
    }
    if (isError && !hasCachedData) {
        return (
            <Typography sx={{ fontSize: 14, color: "error.main", padding: "8px 0" }}>
                {ERROR_MESSAGE}
            </Typography>
        );
    }
    if (modules.length === 0) {
        return (
            <Typography sx={{ fontSize: 14, color: "text.secondary", padding: "8px 0" }}>
                {EMPTY_MESSAGE}
            </Typography>
        );
    }
    return (
        <Box sx={layoutSx}>
            {modules.map((module, index) => {
                const { color, backgroundColor } = resolvePcsModuleStyle(module.status);
                return (
                    <AStatusTile
                        key={`${module.reference ?? "na"}-${index}`}
                        label={PCS_MODULE_LABEL}
                        value={formatModuleNumber(module.reference)}
                        color={color}
                        backgroundColor={backgroundColor}
                        sx={tileSx}
                    />
                );
            })}
        </Box>
    );
};

const BessPcsModulesSection: FunctionComponent<{
    equipmentId: number;
    variant?: BessPcsModulesVariant;
}> = ({ equipmentId, variant = "detail" }) => {
    const { data, isLoading, isError } = useBessPcsModules(equipmentId);
    const modules = data?.data ?? [];
    // RN3b: num refresh automático que falha o react-query preserva o cache. Só tratamos como erro
    // quando não há dado algum — do contrário os tiles desmontariam a cada blip da API.
    const hasCachedData = data != null;

    const hasModules = !isLoading && modules.length > 0;
    const subtitle = hasModules ? buildPcsSummary(modules) : "";

    if (variant === "simple") {
        const content = renderModuleContent(
            isLoading,
            isError,
            hasCachedData,
            modules,
            SIMPLE_GRID_SX,
            SIMPLE_TILE_SX,
            68
        );
        return (
            <Box aria-label="bess-pcs-modules">
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {SIMPLE_SECTION_TITLE}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {SIMPLE_HELPER}
                    </Typography>
                </Box>
                {content}
            </Box>
        );
    }

    const tileSx = modules.length > LEFT_ALIGN_THRESHOLD ? { width: "100%" } : undefined;
    const content = renderModuleContent(
        isLoading,
        isError,
        hasCachedData,
        modules,
        resolveModulesLayoutSx(modules.length),
        tileSx,
        68
    );

    return (
        <ACard
            ariaLabel={"bess-pcs-modules"}
            title={SECTION_TITLE}
            subTitle={subtitle}
            iconTitle={{
                showIcon: true,
                kind: "mui",
                iconMaterialComponent: EnergySavingsLeafOutlinedIcon,
                iconTitleStyle: { color: TITLE_ICON_COLOR, fontSize: 28 },
            }}
            headerControlsPosition={"header"}
            styleProps={{
                cardStyle: { height: "auto" },
                contentStyle: { padding: "0 16px 16px" },
                titleStyle: { alignItems: "center", display: "flex" },
                subTitleStyle: subtitle
                    ? { fontSize: 14, color: "rgba(0, 0, 0, 0.6)", padding: "0 8px 4px" }
                    : { display: "none" },
                headerStyle: { flexDirection: "row" },
                headerActionsStyle: { alignItems: "center", display: "flex" },
            }}
            headerControls={<Legend />}
        >
            {content}
        </ACard>
    );
};

export default BessPcsModulesSection;
